/**
 * The Journal — every closed trade with its story. ROADMAP 9.5.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * CLOSED TRADES ONLY, AND THAT IS WHAT MAKES IT A JOURNAL
 *
 * The Portfolio answers "what am I holding" and the drawer answers "what can
 * I exit". This answers "what did I learn", which is a question about trades
 * that are over: an open position has no exit price, no holding time and no
 * lesson, and including one would make every figure on the page provisional.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE SUMMARY DESCRIBES WHAT IS ON SCREEN, NOT THE ACCOUNT
 *
 * Every tile is computed from the FILTERED rows. A win rate that ignored the
 * filters would contradict the table under it the moment anyone narrowed to
 * one underlying — and a number that disagrees with the list beside it is
 * worse than no number, because it is the one people quote.
 *
 * NET IS THE HEADLINE, GROSS IS THE DETAIL. 6.4's rule reaches the screen:
 * gross and charges are shown side by side so the difference is always
 * explainable, and the big figure is the one that reached the account.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useEffect, useMemo, useState } from 'react'
import { ChevronDown, NotebookPen } from 'lucide-react'
import { Notice } from '@/components/Notice'
import { compactInr } from '@/screens/terminal/quantity'
import { EquityCurve } from './components/EquityCurve'
import { Chip } from './components/Chip'
import { Tile } from './components/Tile'
import { TradeRow } from './components/TradeRow'
import { useJournal } from './hooks/useJournal'
import { holdText, money } from './utils/format'
import { summarise } from './utils/summary'

/**
 * Rows drawn at a time — 9.30.
 *
 * The journal reads the WHOLE history, because the summary tiles are
 * computed from the rows the table shows and totals over a truncated page
 * are wrong rather than merely short. Drawing all of it is the part that
 * does not scale: every row is expandable and carries its legs, so a
 * thousand of them is a thousand subtrees built for the handful anyone
 * reads. Paging the DRAWING costs nothing the numbers depend on.
 */
const ROW_PAGE = 50

const UNDERLYINGS = ['All', 'NIFTY', 'BANKNIFTY', 'SENSEX', 'FINNIFTY'] as const
const RESULTS = ['All', 'Winners', 'Losers'] as const
type Result = (typeof RESULTS)[number]



export default function Journal() {
  const [underlying, setUnderlying] = useState<string>('All')
  const [result, setResult] = useState<Result>('All')
  const [open, setOpen] = useState<string | null>(null)

  const { state, patch } = useJournal(underlying === 'All' ? null : underlying)
  const all = state.kind === 'READY' ? state.trades : []

  const rows = useMemo(
    () =>
      all.filter((t) =>
        result === 'All' ? true : result === 'Winners' ? t.net > 0 : t.net < 0,
      ),
    [all, result],
  )

  /*
   * How many of `rows` are DRAWN. The tiles and the curve read `rows`
   * itself, so narrowing this never changes a number — which is the whole
   * reason the paging lives here and not in the query.
   */
  const [shown, setShown] = useState(ROW_PAGE)
  // Back to the first page when the set changes under it: 300 rows revealed
  // on "All" should not carry over to a filter with 12.
  useEffect(() => setShown(ROW_PAGE), [underlying, result])
  const drawn = useMemo(() => rows.slice(0, shown), [rows, shown])
  const more = rows.length - drawn.length

  /*
   * Computed from the FILTERED rows, so the tiles and the table can never
   * disagree. A trade that broke exactly even is neither a win nor a loss and
   * is counted in neither — rounding it into one would quietly inflate the
   * rate a trader judges themselves by.
   */
  const summary = useMemo(() => summarise(rows), [rows])

  return (
    <div className="jn-page h-full space-y-2 overflow-auto p-4" data-testid="journal-page">
      <header className="jn-page-heading">
        <div>
          <h1 className="text-xl font-extrabold tracking-tight">Journal</h1>
          <p className="mt-0.5 text-[11.5px] text-muted-foreground">
            Review the trade. Understand the outcome.
          </p>
        </div>
        {state.kind === 'READY' && <span className="jn-caption">{rows.length} closed trade{rows.length === 1 ? '' : 's'} · all accounts</span>}
      </header>

      {state.kind === 'FAILED' && (
        <Notice tone="error" title="The journal could not be read." testId="journal-failed">
          This is not the same as having closed nothing. {state.why}
        </Notice>
      )}

      {/*
        The curve reads the FILTERED rows too, for the same reason the tiles
        do: a chart that ignored the filters would plot days the table below
        it is not showing.
      */}
      <div className="jn-summary">
        <EquityCurve trades={rows} />
        <div className="jn-metric-pairs">
          <div className="jn-metric-pair">
            <Tile
              label="NET"
              value={money(summary.net)}
              tone={summary.net >= 0 ? 'up' : 'down'}
              detail={`Gross ${money(summary.gross)} · charges ₹${compactInr(summary.costs)}`}
              testId="journal-net"
            />
            <Tile
              label="WIN RATE"
              // No closed decisions means no win rate, rather than a losing record.
              value={summary.winRate === null ? '—' : `${summary.winRate}%`}
              detail={`${summary.wins} winners · ${summary.losses} losers`}
              testId="journal-winrate"
            />
          </div>
          <div className="jn-metric-pair">
            <Tile
              label="AVG HOLD"
              value={holdText(summary.avgHold)}
              detail={`${rows.length} closed trade${rows.length === 1 ? '' : 's'}`}
              testId="journal-avghold"
            />
            <Tile
              label="UNREVIEWED"
              value={String(summary.unreviewed)}
              testId="journal-unreviewed"
            />
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        {UNDERLYINGS.map((u) => (
          <Chip
            key={u}
            on={underlying === u}
            onClick={() => setUnderlying(u)}
            testId={`journal-filter-${u.toLowerCase()}`}
          >
            {u}
          </Chip>
        ))}
        <span className="mx-1 h-4 w-px bg-border" />
        {RESULTS.map((r) => (
          <Chip
            key={r}
            on={result === r}
            onClick={() => setResult(r)}
            testId={`journal-result-${r.toLowerCase()}`}
          >
            {r}
          </Chip>
        ))}
      </div>

      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <table className="jn-trades w-full table-fixed text-[11.5px]" aria-label="Closed trades">
          <thead>
            <tr className="border-b border-border bg-secondary/40 text-[9px] tracking-wider text-muted-foreground">
              {/* 15+21+12+8+12+11+10+11 = 100, with the chevron inside it. */}
              <th className="w-[15%] py-2 pl-3 text-left font-semibold">CLOSED</th>
              <th className="w-[21%] py-2 text-left font-semibold">STRATEGY</th>
              <th className="w-[12%] py-2 text-left font-semibold">ENTRY / HOLD</th>
              <th className="w-[8%] py-2 text-right font-semibold">RULES</th>
              <th className="w-[12%] py-2 text-right font-semibold">IV IN → OUT</th>
              <th className="w-[11%] py-2 text-right font-semibold">GROSS</th>
              <th className="w-[10%] py-2 text-right font-semibold">CHARGES</th>
              <th className="w-[11%] py-2 text-right font-semibold">NET</th>
              <th className="w-7 py-2 pr-3" />
            </tr>
          </thead>
          <tbody>
            {drawn.map((t) => (
              <TradeRow
                key={t.tradeId}
                trade={t}
                open={open === t.tradeId}
                onToggle={() => setOpen(open === t.tradeId ? null : t.tradeId)}
                onPatched={(next) => patch(t.tradeId, next)}
              />
            ))}
          </tbody>
        </table>

        {/*
          Says what it will add AND what the tiles already cover, because
          the two differ on purpose: the numbers above are the whole
          history, the table below is a window onto it. Without that
          sentence a trader reasonably reads the visible rows as the set
          the net was computed from.
        */}
        {more > 0 && (
          <div className="flex flex-col items-center gap-1 border-t border-border py-3">
            <button
              type="button"
              onClick={() => setShown((n) => n + ROW_PAGE)}
              data-testid="journal-load-more"
              className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-[11.5px] font-medium transition-colors hover:bg-secondary"
            >
              <ChevronDown size={14} aria-hidden="true" />
              Load {Math.min(ROW_PAGE, more)} more
            </button>
            <span className="text-[10.5px] text-muted-foreground">
              Showing {drawn.length} of {rows.length}. The figures above cover all {rows.length}.
            </span>
          </div>
        )}

        {state.kind === 'LOADING' && (
          <p className="p-10 text-center text-[12px] text-muted-foreground">Reading the journal…</p>
        )}
        {state.kind === 'READY' && rows.length === 0 && (
          <div className="flex flex-col items-center gap-2 p-12 text-center" data-testid="journal-empty">
            <NotebookPen size={22} className="text-muted-foreground" />
            <p className="text-[13px] font-semibold">
              {all.length === 0 ? 'No closed trades yet' : 'Nothing matches these filters'}
            </p>
            <p className="max-w-[320px] text-[11.5px] leading-relaxed text-muted-foreground">
              {all.length === 0
                ? 'A trade appears here once every leg is closed.'
                : 'Widen the filters to see the rest.'}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
